import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { getDemos } from '../data'
import { useLocale, useT } from '../i18n/locale.jsx'
import DemoPreview from './DemoPreview'

// Cardurile NU folosesc niciodată verdele fundalului de secțiune (`bg-forest`).
// Ultimul card e ancora (forest-soft, distinct de fundal). Restul ciclează.
const SURFACES = [
  { bg: 'bg-bone', dark: false },
  { bg: 'bg-bone-warm', dark: false },
  { bg: 'bg-honey-soft', dark: false },
]
function skin(i, total) {
  if (i === total - 1) return { bg: 'bg-forest-soft', dark: true }
  return SURFACES[i % SURFACES.length]
}

function ProjectCard({ demo, i, total, sectionProgress }) {
  const ref = useRef(null)
  const t = useT().portfolio

  // Ca în template: pas de scalare 0.02, ultimul card rămâne la 1, scalare din CENTRU.
  const targetScale = 1 - (total - 1 - i) * 0.02
  const scale = useTransform(sectionProgress, [i / total, 1], [1, targetScale])
  const top = `calc(14vh + ${i * 20}px)`

  const { bg, dark } = skin(i, total)
  const txt = dark ? 'text-cream' : 'text-ink'
  const sub = dark ? 'text-cream-soft' : 'text-ink-muted'
  const pill = dark ? 'border-cream/30' : 'border-ink/25'
  const btn = dark
    ? 'border-cream/40 hover:bg-cream hover:text-forest'
    : 'border-ink/30 hover:bg-honey hover:border-honey hover:text-ink'

  return (
    <div ref={ref} className="sticky" style={{ top }}>
      <motion.article
        style={{ scale }}
        className={`group relative mx-auto grid h-[58vh] max-w-site items-stretch gap-2 overflow-hidden rounded-card p-3 md:h-[70vh] md:grid-cols-[1.15fr_0.85fr] md:p-4 ${bg} ${txt} ${dark ? 'border border-forest-line' : ''}`}
      >
        <div className="flex flex-col justify-between p-4 md:p-8">
          <div className="flex items-center gap-2">
            <span className={`rounded-full border ${pill} px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em]`}>Demo</span>
            <span className={`rounded-full border ${pill} px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em]`}>{demo.tag}</span>
          </div>

          <div>
            <h3 className="display text-6xl leading-[0.92] md:text-8xl">{demo.title}</h3>
            <p className={`mt-2 max-w-[24ch] text-2xl md:text-3xl ${sub}`}>{demo.tagline}</p>

            <a
              href={demo.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-7 inline-flex min-h-[44px] items-center gap-2 rounded-full border px-5 font-mono text-xs uppercase tracking-[0.08em] transition-colors duration-300 ease-quint ${btn}`}
            >
              {t.open} <span>→</span>
            </a>
          </div>
        </div>

        {/* Partea vizuală = mini-preview live al demo-ului */}
        <div className="relative overflow-hidden rounded-[10px]">
          <DemoPreview demoKey={demo.key} />
        </div>
      </motion.article>
    </div>
  )
}

export default function Portfolio() {
  const container = useRef(null)
  const locale = useLocale()
  const t = useT().portfolio
  const demos = getDemos(locale)
  const { scrollYProgress } = useScroll({ target: container, offset: ['start start', 'end end'] })

  return (
    <section id="portfolio" data-theme="dark" className="bg-forest px-5 pt-24 md:px-10">
      <div className="mx-auto mb-12 max-w-site">
        <p className="font-mono text-[12px] uppercase tracking-[0.1em] text-cream-muted">{t.eyebrow} ✦</p>
        <p className="mt-2 font-mono text-[12px] text-cream-soft">{t.sub}</p>
      </div>
      <div ref={container} className="flex flex-col gap-[6vh] pb-[34vh]">
        {demos.map((d, i) => (
          <ProjectCard key={d.key} demo={d} i={i} total={demos.length} sectionProgress={scrollYProgress} />
        ))}
      </div>
    </section>
  )
}
